import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Modal — the ONE dialog chrome, ported from views/components/modal.ejs and PFModal (layout.ejs).
 *
 * The four behaviours are the component's, not the page's: Escape closes the top-most dialog, a backdrop
 * click closes it, the page behind stops scrolling, and focus moves into the dialog then returns to
 * whatever opened it. That is why a view never positions, sizes or shows a dialog itself — and why the
 * port keeps all four rather than trusting a page to remember them.
 */
export type ModalProps = {
  /** Required: the overlay's id and the prefix of the title/✕ ids (<id>-title, <id>-x). */
  id: string;
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** 'danger' turns the heading red (a destructive dialog). */
  tone?: 'danger';
  close?: boolean;
  children?: ReactNode;
};

export function Modal({ id, open, onClose, title, subtitle, size = 'md', tone, close = true, children }: ModalProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    // Focus the first focusable thing in the body, falling back to the box itself: a dialog that opens
    // with focus still on the page behind it reads as "nothing happened" to a keyboard or screen reader.
    const box = boxRef.current;
    const focusable = box?.querySelector<HTMLElement>(
      'input, select, textarea, button:not([data-modal-close]), [href], [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? box)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A widget inside the dialog can own the first Escape: the ticker autocomplete closes its suggestion
        // list and only the NEXT press should close the dialog. It marks itself with data-esc-open while it
        // has something to dismiss, and this listener runs in the capture phase — before the widget's own
        // handler — so the check has to be here rather than a stopPropagation in the input.
        if (document.querySelector('[data-esc-open="1"]')) return;
        e.stopPropagation();
        onClose();
        return;
      }
      // Tab is kept inside the dialog while it is open — the EJS PFModal does the same.
      if (e.key !== 'Tab' || !box) return;
      const items = Array.from(
        box.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id={id}
      // `is-open` is what the stylesheet keys on (.modal is display:none, .modal.is-open is flex) — the
      // EJS PFModal adds the same class, and without it the dialog rendered invisible while Escape still
      // "closed" it, so the first version looked like it worked.
      className="modal is-open"
      data-modal
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      onMouseDown={(e) => {
        // Only a press that STARTS on the backdrop closes: a drag that ends there (selecting text in the
        // body) must not throw the dialog away.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`modal-box ${size}`} ref={boxRef} tabIndex={-1}>
        {close ? (
          <button type="button" className="ghost modal-x" id={`${id}-x`} data-modal-close aria-label="Close" onClick={onClose}>
            ✕
          </button>
        ) : null}
        <h3 className={tone === 'danger' ? 't-h2 modal-title neg' : 't-h2 modal-title'} id={`${id}-title`}>
          {title}
        </h3>
        {subtitle ? <p className="muted t-small modal-sub">{subtitle}</p> : null}
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
